<template>
    <div
        class="v-input"
        :class="{ invalid: invalid, 'cad-symbol': currencySymbol == 'C$' }"
    >
        <slot></slot>
        <div
            class="input-container"
            :class="{
                'has-pwd': showPassword,
                'has-label': label,
                clear: clearable,
                'has-value': inputValue
            }"
        >
            <span
                class="label-top"
                :class="{ show: formFocus && placeholder }"
                >{{ placeholder }}</span
            >
            <textarea
                ref="inputEle"
                v-if="tag === 'textarea'"
                :name="name"
                :aria-label="name"
                :placeholder="showPlaceholder"
                v-model.trim="inputValue"
                @blur="onBlur"
                @focus="onFocus"
                @change="onChange"
                autocomplete="off"
                :disabled="disabled"
                v-bind="$attrs"
            ></textarea>
            <input
                ref="inputEle"
                v-else-if="inputType === 'number'"
                type="number"
                :name="name"
                :aria-label="name"
                :placeholder="showPlaceholder"
                :maxlength="maxlength"
                v-model.number="inputValue"
                @blur="onBlur"
                @focus="onFocus"
                @change="onChange"
                autocomplete="off"
                :disabled="disabled"
                v-bind="$attrs"
            />
            <input
                ref="inputEle"
                v-else-if="inputType === 'dollar' || inputType === 'address'"
                type="text"
                :name="name"
                :aria-label="name"
                :placeholder="showPlaceholder"
                :maxlength="maxlength"
                v-model.trim="inputValue"
                @blur="onBlur"
                @focus="onFocus"
                @change="onChange"
                @input="onInput"
                autocomplete="off"
                :disabled="disabled"
                v-bind="$attrs"
            />
            <input
                ref="inputEle"
                v-else-if="inputType === 'tel'"
                :type="inputType"
                :name="name"
                :aria-label="name"
                :placeholder="showPlaceholder"
                v-model="inputValue"
                @input="onInput"
                @blur="onBlur"
                @focus="onFocus"
                @change="onChange"
                autocomplete="off"
                :disabled="disabled"
                v-bind="$attrs"
            />
            <date-picker
                v-else-if="inputType === 'date'"
                :name="name"
                :date.sync="inputValue"
                :type="inputType"
                :placeholder="showPlaceholder"
                format="yyyy/MM/dd"
                valueFormat="yyyy/MM/dd"
                :disabled="disabled"
                v-bind="$attrs"
            ></date-picker>
            <input
                ref="inputEle"
                v-else
                :type="inputType"
                :name="name"
                :aria-label="name"
                :placeholder="showPlaceholder"
                :maxlength="type === 'tel' ? 15 : maxlength"
                v-model.trim="inputValue"
                @blur="onBlur"
                @focus="onFocus"
                @input="onInput"
                @change="onChange"
                autocomplete="off"
                :disabled="disabled"
                v-outsideclick="hideEmailSuggestion"
                v-bind="$attrs"
            />
            <span
                v-if="clearable"
                v-show="inputValue"
                @click.prevent="clear"
                class="iconfont icon-Empty"
            />
            <span
                v-if="showPassword"
                @click.prevent="togglePasswordType"
                class="iconfont"
                :class="
                    inputType === 'password'
                        ? 'icon-password'
                        : 'icon-view-active'
                "
            />
            <div
                v-if="label"
                v-text="label"
                class="prompt"
                :class="{ 'has-value': inputValue }"
            />
        </div>
        <div
            v-if="inputType === 'email'"
            v-show="emailSuggestionShow && emailList.length"
            class="email-suggestion"
        >
            <p
                v-for="item in emailList"
                :key="item.legalStr"
                class="item"
                @click="clickEmailSuggestion(item.legalStr)"
            >
                {{ item.emailStr }}
            </p>
        </div>
        <p
            v-show="invalid || (otherOption && otherOption.errorMsg)"
            class="mg-error"
        >
            {{ errorMsg || (otherOption && otherOption.errorMsg) }}
        </p>
        <slot name="footer"></slot>
    </div>
</template>

<script>
import axios from 'axios';
import { mapState } from 'vuex';
import util, { requestCache } from '../common/util.js';
import { formatValue, setCaret, formats } from '../common/format';
import Autocomplete from '../components/map-here/js/autocomplete';
import globalization from '@/globalization';

const validateRules = {
    required(value, options) {
        const result =
            value !== ''
                ? {
                      valid: true
                  }
                : {
                      valid: false,
                      msg:
                          options['required-msg'] ||
                          globalization.$t('common:validate.fieldRequired')
                  };
        return result;
    },
    email(value, options) {
        const result =
            !value ||
            /^([a-z0-9]+\+)?[a-z0-9\.\'\-_]+@[a-z0-9\-_]+\.[a-z0-9\.\-]+$/i.test(
                value
            )
                ? {
                      valid: true
                  }
                : {
                      valid: false,
                      msg:
                          options['email-msg'] ||
                          globalization.$t('common:validate.emailInvalid')
                  };
        return result;
    },
    tel(value, options) {
        const result =
            !value || /^\d{3}-?\d{3}-?\d{4}$/.test(value)
                ? {
                      valid: true
                  }
                : {
                      valid: false,
                      msg:
                          options['tel-msg'] ||
                          globalization.$t('common:validate.phoneInvalid')
                  };
        return result;
    },
    number(value, options) {
        let result = {
            valid: true
        };
        if (value) {
            if (options.min != null && options.min > value) {
                result = {
                    valid: false,
                    msg:
                        options['min-msg'] ||
                        globalization.$t('common:validate.minNumber', {
                            min: options.min
                        })
                };
            } else if (options.max != null && options.max < value) {
                result = {
                    valid: false,
                    msg:
                        options['max-msg'] ||
                        globalization.$t('common:validate.maxNumber', {
                            min: options.max
                        })
                };
            }
        }
        return result;
    },
    pattern(value, options) {
        const result =
            !value || new RegExp(options.pattern).test(value)
                ? {
                      valid: true
                  }
                : {
                      valid: false,
                      msg:
                          options['pattern-msg'] ||
                          globalization.$t('common:validate.invalidInput')
                  };
        return result;
    },
    async async(value, options) {
        const silence = options.silence;
        if (value) {
            const url = options.async.replace(
                '{value}',
                encodeURIComponent(value)
            );
            let result = {
                valid: true
            };
            await axios
                .get(url)
                .then(() => {
                    requestCache.delete(url, true);
                    if (!silence && options['async-callback']) {
                        options['async-callback'](true);
                    }
                })
                .catch(error => {
                    if (error.error) {
                        !silence &&
                            util.toast(globalization.$t('networkError'));
                        result = {
                            valid: false,
                            msg: ''
                        };
                    } else {
                        result = {
                            valid: options['async-nvalidate'] || false,
                            msg: options['async-msg'] || error.errorInfo
                        };
                        if (!silence && options['async-callback']) {
                            options['async-callback'](false, error, value);
                        }
                    }
                    requestCache.delete(url, true);
                });
            return result;
        } else {
            return {
                valid: true
            };
        }
    },
    equal(value, options) {
        const result =
            value === options.equalDom.value
                ? {
                      valid: true
                  }
                : {
                      valid: false,
                      msg: options['equal-msg'] || ''
                  };
        return result;
    }
};

const maskMap = {
    tel: 'phone',
    dollar: 'number',
    address: 'search'
};

export default {
    props: {
        changeLabelPos: {
            type: Boolean,
            default: false
        },
        showPassword: {
            type: Boolean,
            default: false
        },
        clearable: {
            type: Boolean,
            default: false
        },
        label: {
            type: String,
            default: ''
        },
        tag: {
            type: String,
            default: 'input'
        },
        name: {
            type: String,
            required: true,
            default: ''
        },
        type: {
            type: String,
            default: 'text'
        },
        placeholder: {
            type: String,
            default: ''
        },
        maxlength: {
            type: Number,
            default: 300
        },
        required: {
            type: Boolean,
            default: false
        },
        focus: {
            type: Boolean,
            default: false
        },
        value: [String, Number],
        validateConfig: {
            type: Object,
            default: () => ({})
        },
        otherOption: {
            type: Object,
            default: () => ({})
        },
        register: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        country: {
            type: String,
            default: ''
        }
    },
    inject: ['setVmap'],
    data() {
        return {
            inputValue:
                this.value !== null && this.value !== undefined
                    ? this.value
                    : '',
            inputType: this.type,
            invalid: false,
            errorMsg: '',
            emailSuggestionShow: false,
            rules: [],
            emailSuggestion: [],
            formFocus: false
        };
    },
    computed: {
        ...mapState({
            page: state => state.page.page,
            currencySymbol: state => state.page.currency.symbol
        }),
        compCountry({ page }) {
            return this.country || page.country || 'usa';
        },
        showPlaceholder({ formFocus, placeholder }) {
            return formFocus && placeholder ? '' : placeholder;
        },
        emailList() {
            //  avoid  this.inputValue  is empty resulting in   none  trim  method and report an error 
            if (
                this.emailSuggestion.length &&
                this.inputValue &&
                this.inputValue.trim()
            ) {
                const emailList = [];
                const cuttingLength =
                    (this.otherOption && this.otherOption.cutting) || 20;
                const match = this.inputValue.trim().split('@');
                let emailStr = match[0];
                if (match[0].length > cuttingLength) {
                    emailStr = '...' + match[0].substr(-cuttingLength);
                }
                this.emailSuggestion.forEach(item => {
                    const legalStr = match[0] + '@' + item;
                    if (!match[1]) {
                        match.length <= 2 &&
                            emailList.push({
                                legalStr,
                                emailStr: emailStr + '@' + item
                            });
                    } else {
                        const strictMatch = item.startsWith(match[1]);
                        strictMatch &&
                            emailList.push({
                                legalStr,
                                emailStr: emailStr + '@' + item
                            });
                    }
                });
                return emailList;
            } else {
                return [];
            }
        }
    },
    watch: {
        type(value) {
            this.inputType = value;
            this.getRules();
        },
        value(value) {
            if (this.inputType === 'tel' || this.inputType === 'address') {
                this.inputValue = this.valueFormat(
                    value,
                    maskMap[this.inputType]
                );
            }
            if (this.inputType === 'dollar') {
                this.inputValue = formats.number(value, '0');
            } else {
                this.inputValue = value;
            }
        },
        required() {
            this.getRules();
        }
    },
    created() {
        if (
            ((this.validateConfig.validatePhone !== undefined &&
                this.validateConfig.validatePhone === false) ||
                (this.validateConfig.validatePhone === undefined &&
                    this.page.site &&
                    this.page.site.validatePhone === false)) &&
            this.inputType === 'tel'
        ) {
            this.inputType = 'text';
        }

        this.getRules();
        this.setVmap && this.setVmap(this.name, this);
        this.inputType === 'email' &&
            !this.otherOption.emailSuggestionHide &&
            this.getEmailSuggestion();
    },
    mounted() {
        if (this.inputType === 'tel' || this.inputType === 'address') {
            this.inputValue = this.valueFormat(
                this.inputValue,
                maskMap[this.inputType]
            );
        }
        if (this.inputType === 'dollar') {
            this.inputValue = formats.number(this.inputValue, '0');
        }
        if (this.focus && !util.isMobile) {
            util.timeout({
                handler: () => {
                    this.$refs.inputEle.focus();
                },
                delay: 350,
                unique: true
            });
        }
    },
    methods: {
        getRules() {
            const rules = [];
            if (this.required) {
                rules.push(validateRules.required);
            }
            if (
                this.inputType === 'email' ||
                this.inputType === 'tel' ||
                this.inputType === 'number'
            ) {
                rules.push(validateRules[this.inputType]);
            }
            if (this.type === 'tel' && this.inputType === 'text') {
                rules.push(validateRules.pattern);
            } else {
                if (this.validateConfig.pattern) {
                    rules.push(validateRules.pattern);
                }
                if (this.validateConfig.async) {
                    rules.push(validateRules.async);
                }
            }
            if (this.validateConfig.equal) {
                rules.push(validateRules.equal);
            }
            this.rules = rules;
        },
        /**
         * @param: silence   for true hour ， Collect data without displaying error messages 
         */
        async validate(silence) {
            let validConfig = {
                valid: true
            };
            let validateConfig = this.validateConfig;
            if (this.type === 'tel' && this.inputType === 'text') {
                validateConfig = Object.assign(validateConfig, {
                    pattern: '\\d{6,15}'
                });
            }
            validateConfig.silence = silence;
            for (let i = 0, len = this.rules.length; i < len; i++) {
                const rule = this.rules[i];
                const result = await rule(this.inputValue, validateConfig);
                if (!result.valid) {
                    validConfig = {
                        valid: false,
                        msg: result.msg
                    };
                    !silence && this.showError(result.msg);
                    break;
                }
            }
            return validConfig;
        },
        showError(msg) {
            this.invalid = true;
            this.errorMsg = msg;
            util.timeout({
                handler: () => {
                    this.invalid = false;
                    this.errorMsg = '';
                },
                delay: 3000,
                unique: true
            });
            //  Implement custom rendering error messages 
            this.$emit('error', msg);
        },
        onBlur() {
            this.changeLabelPos && (this.formFocus = false);
            this.$emit('blur', this.name, this.inputValue);
        },
        onFocus() {
            if (this.inputType === 'address') {
                !this.autocomplete && this.initAutocomplete();
            }
            if (this.inputType === 'email' && this.emailList.length) {
                this.emailSuggestionShow = true;
            }
            this.changeLabelPos && (this.formFocus = true);
            this.$emit('focus', this.name, this.inputValue);
        },
        onChange() {
            this.$emit('change', this.name, this.inputValue);
            this.$EventBus.$emit(
                'vInput-change',
                this.name,
                this.inputValue,
                this.$el
            );
        },
        valueFormat(value, mask, doInput = false) {
            let result = formatValue(this.$refs.inputEle, {
                oldValue: this.inputValue,
                value: value,
                mask
            });
            if (!result) {
                return value;
            }
            if (doInput) {
                this.$nextTick(() =>
                    setCaret(this.$refs.inputEle, result.caret)
                );
            }
            return result.value;
        },
        onInput($event) {
            const mask = maskMap[this.inputType];
            if (mask) {
                this.inputValue = this.valueFormat(
                    $event.target.value.trim(),
                    mask,
                    true
                );
            }
            if (this.inputType === 'address') {
                !this.autocomplete && this.initAutocomplete();
            }
            if (this.inputType === 'email' && this.emailList.length) {
                this.emailSuggestionShow = true;
            }
            if (this.type === 'tel' && this.inputType === 'text') {
                this.inputValue = $event.target.value
                    .replace(/[^\d]/g, '')
                    .substr(0, 15);
            }
            this.$emit('input', this.name, this.inputValue);
        },
        initAutocomplete() {
            if (!this.autocomplete) {
                this.autocomplete = true;
                Autocomplete(this.$refs.inputEle, this.compCountry, () => {
                    this.$emit(
                        'addressChange',
                        this.name,
                        (this.inputValue = this.$refs.inputEle.value)
                    );
                });
            }
        },
        getEmailSuggestion() {
            axios
                .get('/api-site/register/emailSuggestion')
                .then(res => {
                    const emailSuggestion = (res && res.data) || [];
                    this.emailSuggestion = emailSuggestion;
                })
                .catch(e => {
                    console.log(e);
                });
        },
        hideEmailSuggestion(e) {
            this.emailSuggestionShow = false;
            if (this.inputType == 'email' && this.inputValue) {
                const result = validateRules.email(this.inputValue, {}); //  Only verify email format 
                !result.valid && this.showError(result.msg);
            }
        },
        clickEmailSuggestion(suggestion) {
            this.inputValue = suggestion;
            this.emailSuggestionShow = false;
        },
        clear() {
            this.inputValue = '';
            this.$refs.inputEle && this.$refs.inputEle.focus();
        },
        togglePasswordType() {
            this.inputType = this.inputType == 'password' ? 'text' : 'password';
            this.$refs.inputEle && this.$refs.inputEle.focus();
        }
    },
    destroyed() {
        this.setVmap && this.setVmap(this.name, null);
    },
    components: {
        datePicker: () =>
            import(
                /* webpackChunkName: "components/date-picker"*/ './date-picker/index.vue'
            )
    }
};
</script>

<style lang="scss">
.v-input {
    position: relative;
    .email-suggestion {
        z-index: 10;
        position: absolute;
        width: 100%;
        max-height: 170px;
        overflow: auto;
        box-shadow: 0 2px 5px 0 rgba(0, 10, 30, 0.1);
        border: 1px solid #ebecf1;
        background: #fff;
        .item {
            width: 100%;
            color: #505050;
            height: 32px;
            line-height: 32px;
            padding: 0 40px 0 10px;
            overflow: hidden;
            white-space: nowrap;
            cursor: pointer;
        }
    }
}
</style>
<style lang="scss" scoped>
.input-container {
    position: relative;
    .label-top {
        position: absolute;
        font: 14px var(--font-normal);
        left: 10px;
        top: 60%;
        transform: translateY(-50%);
        color: #c6c8d1;
        text-transform: capitalize;
        background: #fff;
        padding: 0 20px 0 3px;
        transition: 0.4s all ease;
        opacity: 0;
        z-index: -1;
        &.show {
            top: 0;
            opacity: 1;
            z-index: 1;
        }
    }
    .iconfont {
        position: absolute;
        bottom: 0px;
        color: #c0c4d0;
        padding: 10px;
        right: 0;
        cursor: pointer;
    }
    &.has-pwd .icon-Empty {
        right: 30px;
    }
    &.clear input,
    &.has-pwd input {
        padding-right: 40px;
        &[name='phone'] {
            padding-right: 5px;
        }
    }
    &.clear.has-pwd input {
        padding-right: 70px;
    }
    &.has-label {
        input {
            height: 50px;
        }
        .iconfont {
            padding: 15px 10px;
        }
    }
    .prompt {
        position: absolute;
        left: 10px;
        top: 21px;
        font-size: 1.1em;
        pointer-events: none;
        background-image: linear-gradient(
            to bottom,
            transparent 9px,
            white 9px
        );
        padding: 4px;
        color: var(--color-holder);
        transition: all 0.3s;
    }
    .has-value.prompt {
        font-size: 0.85em;
        top: 0px;
        padding-top: 2px;
        left: 10px;
    }
    input:focus ~ .prompt {
        @extend .has-value.prompt;
        color: var(--color-theme);
    }
    &:not(:hover) input:not(:focus) ~ .icon-Empty {
        visibility: hidden;
    }
    input {
        font-size: inherit;
        &::placeholder {
            color: #a0a3af;
        }
    }
}
</style>
